/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import SearchBox from 'components/common/SearchBox';
import categoryService from 'service/category';
import { showAlert } from 'store/alert/alert.thunk';
import './searchCategory.scss';
import { useAppContext } from 'providers/AppProvider';
import { CopyIcon } from 'assets/img/icons/icons';

export const searchCategoryBreadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: 'search_category',
    active: true
  }
];

const LoadingSpinner = () => (
  <div className="loading-spinner">
    <div className="spinner"></div>
  </div>
);

const SearchCategory = () => {
  const { t, i18n } = useTranslation();
  const dispatch: Dispatch<any> = useDispatch();
  const {
    config: { theme }
  } = useAppContext();

  const {
    data: categoryData,
    isLoading,
    isFetching
  } = useQuery(['GET_CATEGORY'], async () => {
    const res = await categoryService
      .getList({ offset: 0, limit: 100 })
      .then((res: any) => res?.categories);

    return res;
  });

  const buildMenu = (data: any, parentId = undefined) => {
    const menu: any[] = [];
    data.forEach((item: any) => {
      if (item?.parent_id === parentId) {
        const children = buildMenu(data, item?.id);
        const menuItem = {
          id: item?.id,
          label: item?.name?.[i18n?.language] || '', // assuming you want the Russian names
          submenu: children?.length > 0 ? children : undefined,
          showSubmenu: false
        };

        menu.push(menuItem);
      }
    });

    return menu;
  };

  const [menuData, setMenuData] = useState<any[]>([]);
  const [filteredMenuData, setFilteredMenuData] = useState<any[]>([]);
  const [searchedValue, setSearchedValue] = useState('');

  useEffect(() => {
    if (categoryData) {
      const initialMenuData = buildMenu(categoryData);
      setMenuData(initialMenuData);
      setFilteredMenuData(initialMenuData); // Initialize with full menu data
    }
  }, [categoryData]);

  const toggleSubMenu = (e: any, categoryId: any) => {
    e.stopPropagation();

    // Ensure toggle works when clicking the chevron icon
    const targetElement = e.target.closest('li');
    if (!targetElement) return;

    const submenu = targetElement.querySelector('ul');

    if (!submenu) return;

    if (submenu.style.display === 'none' || !submenu.style.display) {
      submenu.style.display = 'block';
    } else {
      submenu.style.display = 'none';
    }

    const updateShowSubmenuRecursively = (items: any[]) => {
      return items.map(item => {
        if (item.id === categoryId && item.submenu) {
          item.showSubmenu = !item.showSubmenu; // Toggle showSubmenu state
        }
        if (item.submenu) {
          item.submenu = updateShowSubmenuRecursively(item.submenu);
        }
        return item;
      });
    };

    const updatedMenuData = updateShowSubmenuRecursively(menuData);
    setMenuData(updatedMenuData);

    // Sync the filteredMenuData with updated menuData
    const filteredData = filterMenuData(searchedValue, updatedMenuData);
    setFilteredMenuData(filteredData);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        dispatch(
          showAlert({
            title: `${t('copied')}: ${text}`,
            type: 'success'
          })
        );
      })
      .catch(err => console.error('Error copying text: ', err));
  };

  const renderSubMenu = (subMenu: any) => {
    return (
      <ul className="submenu">
        {subMenu.map((subItem: any, index: any) => (
          <li key={index} onClick={e => toggleSubMenu(e, subItem.id)}>
            {subItem.submenu ? (
              <FontAwesomeIcon
                icon={subItem.showSubmenu ? faChevronUp : faChevronDown}
                transform={{ size: 14 }}
                style={{ marginRight: '5px' }}
                onClick={e => toggleSubMenu(e, subItem.id)}
              />
            ) : null}
            {subItem.label}

            <span
              className="copy-icon"
              onClick={e => {
                e.stopPropagation();
                copyToClipboard(subItem.label);
              }}
            >
              <CopyIcon />
            </span>

            {subItem.submenu && renderSubMenu(subItem.submenu)}
          </li>
        ))}
      </ul>
    );
  };

  const filterMenuData = (query: string, data = menuData) => {
    if (!query) {
      return data; // Return full menu if no search query
    }

    const filterRecursively = (items: any[]) => {
      return items
        .map(item => {
          const matches = item.label
            .toLowerCase()
            .includes(query.toLowerCase());

          // Recursively filter submenu items
          const filteredChildren = item.submenu
            ? filterRecursively(item.submenu)
            : [];

          // If parent matches OR any of its children matches, include the parent with all its children
          if (matches || filteredChildren.length > 0) {
            return {
              ...item,
              submenu: item.submenu // Include all children
            };
          }
          return null;
        })
        .filter(Boolean); // Remove null values
    };

    return filterRecursively(data);
  };

  const handleSearchChange = (e: any) => {
    const query = e.target.value;
    setSearchedValue(query);

    const filteredData = filterMenuData(query, menuData); // Pass the original menuData
    setFilteredMenuData(filteredData);
  };

  return (
    <div>
      <PageBreadcrumb items={searchCategoryBreadcrumbItems} />

      <h2 className="mb-5">{t('search_category')}</h2>

      <div
        className={theme === 'dark' ? 'main-container-dark' : 'main-container'}
      >
        <div className="fixed-input-container">
          <SearchBox
            type="text"
            className="search-input"
            placeholder={t('search')}
            value={searchedValue}
            onChange={handleSearchChange}
          />
        </div>
        {isLoading || isFetching ? (
          <LoadingSpinner />
        ) : (
          <ul className="main-cat">
            {filteredMenuData.map((item, index) => (
              <li key={index} onClick={e => toggleSubMenu(e, item.id)}>
                {item.submenu ? (
                  <FontAwesomeIcon
                    icon={item.showSubmenu ? faChevronUp : faChevronDown}
                    transform={{ size: 14 }}
                    style={{ marginRight: '5px' }}
                    onClick={e => toggleSubMenu(e, item.id)}
                  />
                ) : null}
                {item.label}

                <span
                  className="copy-icon"
                  onClick={e => {
                    e.stopPropagation();
                    copyToClipboard(item.label);
                  }}
                >
                  <CopyIcon />
                </span>

                {item.submenu && renderSubMenu(item.submenu)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SearchCategory;
